import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSideNav } from '../contexts/SideNavContext';
import './SideNavLayout.css';

const COLLAPSE_RAIL_STORAGE_KEY = 'cfc-side-nav-collapse-rail-collapsed';

/**
 * Generic two-panel layout: left nav + right content.
 * Nav can be collapsed to a narrow strip to give the content panel more horizontal space.
 * Used by Settings, Lists, Reports, Client layout, SuperAdmin.
 *
 * @param {React.ReactNode} nav - Nav content (header + links/groups)
 * @param {React.ReactNode} [children] - Main content. Defaults to <Outlet /> for route nesting.
 * @param {string} [navClassName] - Optional extra class(es) for the nav (e.g. for wider nav in Reports).
 * @param {boolean} [isolateCollapse] - If true, use local collapse state (e.g. Reports hub's own nav). If false, use shared context (main menu). Collapse/expand is user-only (hamburger); no route-based auto-collapse.
 * @param {boolean} [collapseRail] - If true, show a vertical control strip on the nav edge to collapse/expand (implies local state; use with isolateCollapse). Persists collapsed state in sessionStorage.
 * @param {string} [collapseRailStorageKey] - sessionStorage key for collapse state (use a unique key per hub so Reports vs Client do not share state).
 * @param {string} [collapseRailExpandTitle] - `title` on the rail button when nav is collapsed.
 * @param {string} [collapseRailCollapseTitle] - `title` on the rail button when nav is expanded.
 * @param {string} [collapseRailExpandAria] - `aria-label` when collapsed (expand action).
 * @param {string} [collapseRailCollapseAria] - `aria-label` when expanded (collapse action).
 */
function SideNavLayout({
  nav,
  children,
  navClassName,
  isolateCollapse = false,
  collapseRail = false,
  collapseRailStorageKey = COLLAPSE_RAIL_STORAGE_KEY,
  collapseRailExpandTitle = 'Show panel',
  collapseRailCollapseTitle = 'Hide panel',
  collapseRailExpandAria = 'Expand side navigation',
  collapseRailCollapseAria = 'Collapse side navigation',
}) {
  const shared = useSideNav();
  const useLocalCollapse = isolateCollapse || collapseRail;
  const [localCollapsed, setLocalCollapsed] = useState(() => {
    if (!collapseRail) return false;
    try {
      return window.sessionStorage.getItem(collapseRailStorageKey) === '1';
    } catch (_) {
      return false;
    }
  });

  const collapsed = useLocalCollapse ? localCollapsed : shared.collapsed;
  const toggleNav = useLocalCollapse
    ? () =>
        setLocalCollapsed((c) => {
          const next = !c;
          if (collapseRail) {
            try {
              window.sessionStorage.setItem(collapseRailStorageKey, next ? '1' : '0');
            } catch (_) {}
          }
          return next;
        })
    : shared.toggleNav;

  const navClasses = [
    'side-nav-layout-nav',
    navClassName,
    collapsed ? 'side-nav-layout-nav--collapsed' : '',
    collapseRail ? 'side-nav-layout-nav--collapse-rail' : '',
  ].filter(Boolean).join(' ');

  const isMainNav = navClassName && navClassName.includes('side-nav-layout-nav--main');
  const wrapperClasses = [
    'side-nav-layout-wrapper',
    isMainNav ? 'side-nav-layout-wrapper--main' : '',
  ].filter(Boolean).join(' ');

  const navBody = collapseRail ? (
    <div className="side-nav-layout-nav-with-rail">
      <div className="side-nav-layout-nav-inner">{nav}</div>
      <div className="side-nav-layout-collapse-rail-strip">
        <button
          type="button"
          className="side-nav-layout-collapse-rail-btn no-print"
          onClick={toggleNav}
          aria-expanded={!collapsed}
          title={collapsed ? collapseRailExpandTitle : collapseRailCollapseTitle}
          aria-label={collapsed ? collapseRailExpandAria : collapseRailCollapseAria}
        >
          <svg className="side-nav-layout-collapse-rail-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            {collapsed ? (
              <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            )}
          </svg>
        </button>
      </div>
    </div>
  ) : (
    <div className="side-nav-layout-nav-inner">{nav}</div>
  );

  return (
    <div className={wrapperClasses}>
      <div className="side-nav-layout">
        <nav className={navClasses} aria-label="Section navigation">
          {navBody}
        </nav>
        <div className="side-nav-layout-content">
          {children ?? <Outlet />}
        </div>
      </div>
    </div>
  );
}

export default SideNavLayout;
